<template>
  <div>
    <v-container fluid>
      <v-row dense class="pb-0">
        <v-col cols="2">
          <v-menu transition="scale-transition" offset-y v-model="dateMenu">
            <template v-slot:activator="{ on }">
              <v-text-field label="Month" hide-details dense readonly v-on="on" v-model="monthAsDate" outlined append-icon="mdi-menu-down" @click:append="dateMenu=true"/>
            </template>
            <v-date-picker v-model="month" type="month" no-title @change="refresh"/>
          </v-menu>
        </v-col>
        <v-col cols="2">
          <v-select :items="licenses" label="License" v-model="license" @change="refresh" dense hide-details outlined class="ml-6"/>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml('Spirit return details',buildReport())"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 180px)">
      <span v-html="buildReport()"/>
    </v-card>

  </div>
</template>

<style>
.report table {
  padding-right:14px;
}
.report tbody tr td{
  border:none !important;
  height:24px !important;
}
.report tbody tr:hover {
  background-color: #fff !important;
}
</style>

<script>
import {format,add,lastDayOfMonth} from 'date-fns'
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'

export default {
  name: 'ReportsExciseSpiritsDetails',
  mixins: [printHtml],
  data() {
    return {
      dateMenu: false,
      month: '',
      license: '',
      loading: true,
      report: {lines:[]},
      transactions: []
    }
  },
  computed: {
    monthAsDate() {
      return this.$formatDate(this.month+'-01', 'MMMM, yyyy');
    },
    licenses() {
      return [...new Set(this.$store.getters.sites(true, {B266:true}).map(site => site.spiritsLicense))];
    },
    company() {
      return this.$store.getters.company;
    }
  },
  async created() {
    this.$emit('name', 'Spirits Licensee return details')
    this.month = format(add(new Date(), {months:-1}), 'yyyy-MM');
    this.license = this.licenses[0];
    this.refresh();
  },
  methods: {
    async refresh() {
      this.loading = true;
      let to = format(lastDayOfMonth(add(new Date(this.month+'-01'),{days:1})), 'yyyy-MM-dd');
      const results = await this.$apiCall(`query ReportsExciseSpiritsDetails($license:String!,$date:Date!,$to:Date!) {
                                              exciseSpiritsReport(license:$license,date:$date) {
                                                openingInventory,totalAdditions,totalReductions,closingInventory,lines{excise,laa}
                                              }
                                              spiritsActivity(from:$date,to:$to,userLicense:false,license:$license) {
                                                date,description,volume,alcoholName,vesselType,vesselName,excise,comments
                                              }
                                            }`,
                                          {license:this.license,date:this.month+'-01',to});
      if (!results.error) {
        this.report = results.data.exciseSpiritsReport;
        this.transactions = results.data.spiritsActivity;
      }
      this.loading = false;
    },

    buildReport() {
      const lines = [`<style>
                        @page {size:landscape}
                        .report-excise-spirits-details {font-family: Roboto, sans-serif; font-size:12px;width:100%;border-spacing:0}
                        .report-excise-spirits-details body {font-family: Roboto, sans-serif; font-size:14px}
                        .report-excise-spirits-details th,td {height:36px;padding:0 16px;vertical-align:middle}
                        .report-excise-spirits-details .title {font-size:18px;text-transform:uppercase}
                        .report-excise-spirits-details .sup-title {font-weight:normal;font-size:22px}
                        .report-excise-spirits-details .sub-title {font-weight:normal;height:inherit;padding-bottom:20px;font-size:14px}
                        .report-excise-spirits-details .header th {border-bottom:1px solid #000;margin-bottom:20px}
                        .report-excise-spirits-details .text-left {text-align:left}
                        .report-excise-spirits-details .text-right {text-align:right}
                        .report-excise-spirits-details .font-weight-bold {font-weight:bold}
                        .report-excise-spirits-details .total-row {font-weight:bold}
                      </style>
                      <table class="report-excise-spirits-details">
                        <thead>
                          <tr>
                            <th colspan="6" class="sup-title">${this.$store.getters.company.name}</th>
                          </tr>
                          <tr><th colspan="6" class="title">Excise spirits details</th></tr>
                          <tr><th colspan="6" class="sub-title">${this.license} ${this.monthAsDate}</th></tr>
                          <tr class="header">
                            <th width="10%" class="text-left"></th>
                            <th width="10%" class="text-left">Date</th>
                            <th width="30%" class="text-left">Spirit</th>
                            <th width="30%" class="text-left">Description</th>
                            <th width="10%" class="text-right">LAA</th>
                            <th width="10%" class="text-right">Total LAA</th>
                          </tr>
                        </thead>
                        <tbody>`
      ];
      lines.push(`<tr class="font-weight-bold"><td colspan="5" class="text-right">Opening inventory</td><td class="text-right">${this.laa(this.report.openingInventory)}</td>`);
      lines.push(`<tr class="font-weight-bold"><td colspan="6">Additions to bulk spirits inventory</td>`);
      lines.push(...this.details('SpiritsAdditionSpiritsProduced','Spirits produced'));
      lines.push(...this.details('SpiritsAdditionReceivedFromLicensees','Received from other spirits licensees and licensed users'));
      lines.push(...this.details('SpiritsAdditionImported','Imported bulk spirits'));
      lines.push(...this.details('SpiritsAdditionPackagedSpiritsReturnedToBulk','Packaged spirits returned to bulk spirits inventory'));
      lines.push(`<tr class="font-weight-bold"><td colspan="5" class="text-right">Total additions</td><td class="text-right">${this.laa(this.report.totalAdditions)}</td>`);

      lines.push(`<tr class="font-weight-bold"><td colspan="6">Reductions to bulk spirits inventory</td>`);
      lines.push(...this.details('SpiritsReductionRemovedToSpiritsLicensee','Removed for delivery to Spirits licensees'));
      lines.push(...this.details('SpiritsReductionRemovedToLicensedUsersDomestic','Removed for delivery to licensed users - domestic spirits'));
      lines.push(...this.details('SpiritsReductionRemovedToLicensedUsersImported','Removed for delivery to licensed users - imported spirits'));
      lines.push(...this.details('SpiritsReductionDenaturedSpiritsFuelDA2F','To produce denatured alcohol (DA) Fuel grades DA-2F'));
      lines.push(...this.details('SpiritsReductionDenaturedSpiritsFuelDA2G','To produce denatured alcohol (DA) Fuel grades DA-2G'));
      lines.push(...this.details('SpiritsReductionDenaturedSpiritsOther','To produce denatured alcohol (DA) Other grades'));
      lines.push(...this.details('SpiritsReductionSpeciallyDenaturedSpirits','To produce specially denatured alcohol (SDA)'));
      lines.push(...this.details('SpiritsReductionExported','Exported bulk Spirits'));
      lines.push(...this.details('SpiritsReductionToPackagingNonDutyPaidGT7','Removed to packaging activities - Packaged: non-duty-paid Containing more than 7% of absolute ethyl alcohol by volume'));
      lines.push(...this.details('SpiritsReductionToPackagingNonDutyPaidLE7','Removed to packaging activities - Packaged: non-duty-paid Containing not more than 7% of absolute ethyl alcohol by volume'));
      lines.push(...this.details('SpiritsReductionToPackagingNonDutyPaidMarkedSpecialContainers','Removed to packaging activities - Packaged: non-duty-paid Packaged in marked special containers'));
      lines.push(...this.details('SpiritsReductionToPackagingDutyPaid','Removed to packaging activities - Packaged: duty-paid'));
      lines.push(...this.details('SpiritsReductionBulkSpiritsReturnedToSpiritProduction','Bulk spirits returned to production'));
      lines.push(...this.details('SpiritsReductionOtherNonDutyPaid','Removed for other purposes Non-duty-paid (specify)'));
      lines.push(...this.details('SpiritsReductionOtherDutyPaid','Removed for other purposes Duty-paid (specify)'));
      lines.push(`<tr class="font-weight-bold"><td colspan="5" class="text-right">Total reductions</td><td class="text-right">${this.laa(this.report.totalReductions)}</td>`);

      lines.push(...this.details('SpiritsInventoryAdjustment','Inventory adjustments (+ or -)'));
      lines.push(`<tr class="font-weight-bold"><td colspan="5" class="text-right">Closing inventory</td><td class="text-right">${this.laa(this.report.closingInventory)}</td>`);

      lines.push(`</tbody>`);
      lines.push(`</table>`);
      return lines.join('');
    },
    laa(value) {
      return this.$number(value, 3);
    },
    line(excise, noFormat=false) {
      const entry = this.report.lines.find(line => line.excise === excise);
      return entry ? (noFormat ? entry.laa : this.laa(entry.laa)) : '0.000';
    },
    details(excise, description) {
      const negative = excise.startsWith('SpiritsReduction') ? -1 : 1;

      const lines = [];
      lines.push(`<tr><td colspan="5">${description}</td><td class="text-right">${this.line(excise)}</td>`);
      for (const transaction of this.transactions)
        if (transaction.excise === excise)
          lines.push(`<tr>
                        <td/>
                        <td>${this.$formatDate(transaction.date)}</td>
                        <td>${transaction.alcoholName}</td>
                        <td>${transaction.description}${transaction.comments?`<div><em>${transaction.comments}</em></div>`:''}</td>
                        <td class="text-right">${this.laa(transaction.volume*negative)}</td>
                        <td/>
                      </tr>`);
      return lines;
    },
    exportCSV() {
      const lines = [ `"${this.$store.getters.company.name}",,,,,`,
                      `"Excise spirits details",,,,,`,
                      `"${this.license} ${this.monthAsDate}",,,,,`,
                      `,"Date","Spirit","Description","LAA","Total LAA"`,
                      `,,,,"Opening inventory",${this.report.openingInventory}`,
                      `"Additions to bulk spirits inventory",,,,,`,
                      `${this.exportDetailsCSV('SpiritsAdditionSpiritsProduced','Spirits produced')}`,
                      `${this.exportDetailsCSV('SpiritsAdditionReceivedFromLicensees','Received from other spirits licensees and licensed users')}`,
                      `${this.exportDetailsCSV('SpiritsAdditionImported','Imported bulk spirits')}`,
                      `${this.exportDetailsCSV('SpiritsAdditionPackagedSpiritsReturnedToBulk','Packaged spirits returned to bulk spirits inventory')}`,
                      `,,,,"Total additions",${this.report.totalAdditions}`,
                      `"Reductions to bulk spirits inventory",,,,,`,
                      `${this.exportDetailsCSV('SpiritsReductionRemovedToSpiritsLicensee','Removed for delivery to Spirits licensees')}`,
                      `${this.exportDetailsCSV('SpiritsReductionRemovedToLicensedUsersDomestic','Removed for delivery to licensed users - domestic spirits')}`,
                      `${this.exportDetailsCSV('SpiritsReductionRemovedToLicensedUsersImported','Removed for delivery to licensed users - imported spirits')}`,
                      `${this.exportDetailsCSV('SpiritsReductionDenaturedSpiritsFuelDA2F','To produce denatured alcohol (DA) Fuel grades DA-2F')}`,
                      `${this.exportDetailsCSV('SpiritsReductionDenaturedSpiritsFuelDA2G','To produce denatured alcohol (DA) Fuel grades DA-2G')}`,
                      `${this.exportDetailsCSV('SpiritsReductionDenaturedSpiritsOther','To produce denatured alcohol (DA) Other grades')}`,
                      `${this.exportDetailsCSV('SpiritsReductionSpeciallyDenaturedSpirits','To produce specially denatured alcohol (SDA)')}`,
                      `${this.exportDetailsCSV('SpiritsReductionExported','Exported bulk Spirits')}`,
                      `${this.exportDetailsCSV('SpiritsReductionToPackagingNonDutyPaidGT7','Removed to packaging activities - Packaged: non-duty-paid Containing more than 7% of absolute ethyl alcohol by volume')}`,
                      `${this.exportDetailsCSV('SpiritsReductionToPackagingNonDutyPaidLE7','Removed to packaging activities - Packaged: non-duty-paid Containing not more than 7% of absolute ethyl alcohol by volume')}`,
                      `${this.exportDetailsCSV('SpiritsReductionToPackagingNonDutyPaidMarkedSpecialContainers','Removed to packaging activities - Packaged: non-duty-paid Packaged in marked special containers')}`,
                      `${this.exportDetailsCSV('SpiritsReductionToPackagingDutyPaid','Removed to packaging activities - Packaged: duty-paid')}`,
                      `${this.exportDetailsCSV('SpiritsReductionBulkSpiritsReturnedToSpiritProduction','Bulk spirits returned to production')}`,
                      `${this.exportDetailsCSV('SpiritsReductionOtherNonDutyPaid','Removed for other purposes Non-duty-paid (specify)')}`,
                      `${this.exportDetailsCSV('SpiritsReductionOtherDutyPaid','Removed for other purposes Duty-paid (specify)')}`,
                      `,,,,"Total reductions",${this.report.totalReductions}`,
                      `${this.exportDetailsCSV('SpiritsInventoryAdjustment','Inventory adjustments (+ or -)')}`,
                      `,,,,"Closing inventory",${this.report.closingInventory}`
                    ];

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Excise spirits details.csv');
    },
    exportDetailsCSV(excise, description) {
      const negative = excise.startsWith('SpiritsReduction') ? -1 : 1;

      const lines = [];
      lines.push(`"${description}",,,,,${this.line(excise, true)}`);
      for (const transaction of this.transactions)
        if (transaction.excise === excise)
          lines.push(`,"${this.$formatDate(transaction.date)}","${transaction.alcoholName}","${transaction.description}",${transaction.volume*negative}`);

      return lines.join('\r\n');
    }
  },
}
</script>